import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from '@aiia/portal-core';

const STORAGE_KEY = 'aiia_welcome_seen';

export default function WelcomeModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const handleGoToDemo = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
    navigate(`/${lang || 'ka'}/examples`);
  };

  const steps = [
    // Step 0: Welcome
    <div key="welcome" className="space-y-4">
      <div className="text-center mb-6">
        <div className="text-5xl mb-3">&#x1F44B;</div>
        <h2 className="text-xl font-bold text-gray-900">{t('welcome.title')}</h2>
        <p className="text-gray-600 mt-2">{t('welcome.subtitle')}</p>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">&#x1F3AF;</span>
          <div>
            <h3 className="font-semibold text-indigo-900">{t('welcome.whatIsAiia')}</h3>
            <p className="text-sm text-indigo-700 mt-1">{t('welcome.whatIsAiiaDesc')}</p>
          </div>
        </div>
      </div>
    </div>,

    // Step 1: Demo
    <div key="demo" className="space-y-4">
      <div className="text-center mb-4">
        <div className="text-4xl mb-2">&#x1F680;</div>
        <h2 className="text-xl font-bold text-gray-900">{t('welcome.demoTitle')}</h2>
      </div>

      <p className="text-gray-600 text-sm">{t('welcome.demoDesc')}</p>

      <div className="grid gap-2">
        {[
          { icon: '\uD83D\uDED2', title: t('welcome.demoEcommerce'), desc: t('welcome.demoEcommerceDesc') },
          { icon: '\uD83C\uDFE5', title: t('welcome.demoClinic'), desc: t('welcome.demoClinicDesc') },
          { icon: '\uD83C\uDF55', title: t('welcome.demoRestaurant'), desc: t('welcome.demoRestaurantDesc') },
        ].map((s, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
            <span className="text-2xl">{s.icon}</span>
            <div>
              <h4 className="font-medium text-gray-900 text-sm">{s.title}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
        <span className="font-medium text-amber-800">&#x26A0;&#xFE0F; {t('welcome.demoNote')}</span>
        <p className="text-amber-700 mt-1 text-xs">{t('welcome.demoNoteDesc')}</p>
      </div>
    </div>,

    // Step 2: How it works
    <div key="how" className="space-y-4">
      <div className="text-center mb-4">
        <div className="text-4xl mb-2">&#x2699;&#xFE0F;</div>
        <h2 className="text-xl font-bold text-gray-900">{t('welcome.howItWorks')}</h2>
      </div>

      <div className="space-y-3">
        {[
          { num: '1', icon: '\uD83D\uDD11', title: t('welcome.step1Title'), desc: t('welcome.step1Desc') },
          { num: '2', icon: '\uD83D\uDCAC', title: t('welcome.step2Title'), desc: t('welcome.step2Desc') },
          { num: '3', icon: '\uD83D\uDEE0', title: t('welcome.step3Title'), desc: t('welcome.step3Desc') },
          { num: '4', icon: '\uD83E\uDDE9', title: t('welcome.step4Title'), desc: t('welcome.step4Desc') },
          { num: '5', icon: '\uD83D\uDE80', title: t('welcome.step5Title'), desc: t('welcome.step5Desc') },
        ].map((s) => (
          <div key={s.num} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
              {s.num}
            </div>
            <div>
              <h4 className="font-medium text-gray-900 text-sm">{s.icon} {s.title}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>,
  ];

  const isLastStep = step === steps.length - 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <div className="flex items-center justify-between">
          {/* Step indicators */}
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === step ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {t('welcome.skip')}
            </button>
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                {t('welcome.back')}
              </button>
            )}
            {isLastStep ? (
              <button
                onClick={handleGoToDemo}
                className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                {t('welcome.goToDemo')}
              </button>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                {t('welcome.next')}
              </button>
            )}
          </div>
        </div>
      }
    >
      {steps[step]}
    </Modal>
  );
}
